import { ICreatePersonalRate, IPersonalRates } from '@/api/types/offers';

export interface IPersonalRateAmounts {
    revenue: number | string | null;
    payout: number | string | null;
}

export interface IPersonalRateWaitOptions {
    signal: AbortSignal;
    interval?: number;
    timeout?: number;
}

export interface IPersonalRateWaitResult<T = IPersonalRates> {
    rate: T | null;
    isApplied: boolean;
}

// Строка правки из списка: те же доход, выплата и дата, что ушли в POST
export interface IEditedPersonalRate extends IPersonalRateAmounts {
    id: number;
    start_date?: string;
}

export const UPDATE_POLL_INTERVAL_MS = 2000;
export const UPDATE_POLL_TIMEOUT_MS = 30000;

// offers-service выполняет правку сразу, если дата начала не дальше 5 минут впереди
// (UpdatePersonalRatesHandler::isExecuteTaskNow), более позднюю подбирает крон
const EXECUTE_NOW_WINDOW_MS = 5 * 60 * 1000;

export const getPersonalRateAmounts = (rate: IPersonalRates): IPersonalRateAmounts => ({
    revenue: rate.personal_rates.revenue.amount,
    payout: rate.personal_rates.payout.amount,
});

// Поля ввода отдают суммы то числом, то строкой ("5.00", "6.0000"), поэтому сравниваются числа
export const isPersonalRateChanged = (saved: IPersonalRateAmounts, edited: IPersonalRateAmounts): boolean =>
    Number(saved.revenue) !== Number(edited.revenue) || Number(saved.payout) !== Number(edited.payout);

// Строка списка уходит в сохранение, только пока её суммы отличаются от загруженных.
// Как и в карточке, одна смена даты без смены сумм - не изменение
export const applyEditedPersonalRate = (
    edited: ICreatePersonalRate[],
    rate: ICreatePersonalRate,
    saved: IPersonalRateAmounts,
): ICreatePersonalRate[] => {
    const others = edited.filter(({ id }) => id !== rate.id);
    const amounts = { revenue: rate.revenue ?? null, payout: rate.payout ?? null };

    return isPersonalRateChanged(saved, amounts) ? [...others, rate] : others;
};

export const isPersonalRateDueNow =(startDate?: string, now: Date = new Date()): boolean => {
    const date = new Date(String(startDate).replace(' ', 'T'));
    return Number.isNaN(date.getTime()) || date.getTime() - now.getTime() <= EXECUTE_NOW_WINDOW_MS;
};

// Правка применена, когда выполнены все задачи, появившиеся в истории после неё
export const isPersonalRateUpdateApplied = (rate: IPersonalRates, knownTaskIds: number[]): boolean => {
    const newTasks = rate.rates_history.filter(({ id }) => !knownTaskIds.includes(id));
    return newTasks.length > 0 && newTasks.every(({ is_done }) => is_done);
};

const delay = (ms: number, signal: AbortSignal): Promise<void> => new Promise((resolve) => {
    const onAbort = (): void => {
        clearTimeout(timer);
        resolve();
    };
    const timer = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
});

// POST правки только ставит задачу в очередь offers-service, цену переписывает консьюмер.
// Перечитываем цену, пока правка не применится, но не дольше timeout
export async function waitPersonalRateUpdate<T = IPersonalRates>(
    load: () => Promise<T>,
    isApplied: (rate: T) => boolean,
    {
        signal,
        interval = UPDATE_POLL_INTERVAL_MS,
        timeout = UPDATE_POLL_TIMEOUT_MS,
    }: IPersonalRateWaitOptions,
): Promise<IPersonalRateWaitResult<T>> {
    let rate: T | null = null;

    for (let waited = interval; waited <= timeout; waited += interval) {
        await delay(interval, signal);
        if (signal.aborted) break;

        rate = await load();
        if (isApplied(rate)) {
            return { rate, isApplied: true };
        }
    }

    return { rate, isApplied: false };
}

// Список перечитывается после правки, когда у каждой цены, которую бэкенд выполняет сразу,
// суммы стали отправленными. Задачи в списке не видно, поэтому сравниваются суммы, а не история.
// На круг опроса - один запрос текущей страницы. Цену, которой на странице больше нет (ушла
// по сортировке или фильтру), не ждём: перечитанная таблица её старую сумму не покажет
export async function waitPersonalRatesApplied(
    rates: IEditedPersonalRate[],
    loadPage: () => Promise<IPersonalRates[]>,
    options: IPersonalRateWaitOptions,
): Promise<boolean> {
    const dueNow = rates.filter(({ start_date }) => isPersonalRateDueNow(start_date));
    if (dueNow.length === 0) {
        return true;
    }

    const { isApplied } = await waitPersonalRateUpdate(
        loadPage,
        (page) => dueNow.every((sent) => {
            const loaded = page.find(({ personal_rates }) => personal_rates.id === sent.id);
            return !loaded || !isPersonalRateChanged(sent, getPersonalRateAmounts(loaded));
        }),
        options,
    );

    return isApplied;
}
